import { Injectable } from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';

import { ATTACHMENT_KIND_SPECS, AttachmentKind } from '../interfaces';

export const EMAIL_REGEX = /^(([^<>+()\[\]\\.,;:\s@"-#$%&=]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/;

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  constructor() { }

  unixTimestampToDate(timestamp: number) {
    let date = new Date(timestamp * 1000);
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    });
  }

  parsePrice(price: number, showSymbol: boolean = true) {
    if (price >= 0) {
      let priceStr = `$${price.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
      return showSymbol ? priceStr : priceStr.replace('$', '');
    }
    else {
      let priceStr = `-$${Math.abs(price).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
      return showSymbol ? priceStr : priceStr.replace('$', '');
    }
  }

  parseRutString(rutValue: string): string {
    let lastChar = rutValue.slice(-1);
    if (lastChar !== 'k' && lastChar !== 'K' && isNaN(parseInt(lastChar))) {
      rutValue = rutValue.slice(0, -1);
    }
    else {
      let prevChar = rutValue.slice(-2, -1);
      if (isNaN(parseInt(prevChar)) && prevChar) {
        rutValue = rutValue.slice(0, -1);
      }
    }
    rutValue = rutValue.toUpperCase();
    let rut = rutValue.replace(/\./g, '').replace(/-/g, '');
    if (rut.length > 1) {
      rut = rut.slice(0, -1) + '-' + rut.slice(-1);
    }
    let rutParts = rut.split('-');
    rutParts[0] = rutParts[0].replace(/\./g, '');
    rutParts[0] = rutParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    rut = rutParts.join('-');
    return rut.trim();
  }

  rutValidatorFn: ValidatorFn = (control: FormControl) => {
    // This function validates a chilean RUT. It can return two errors:
    // - pattern: if the RUT format is incorrect
    // - invalid: if the DV is incorrect
    let rut = control.value;
    if (!rut) {
      return null;
    }
    const cleanRut = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
    const rutFormatRegex = /^\d{7,8}[0-9K]$/;
    if (!rutFormatRegex.test(cleanRut)) {
      return { 'pattern': true };
    }
    const rutNumber = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1);
    if (dv !== this.calculateDV(rutNumber)) {
      return { 'invalid': true };
    }
    return null;
  }

  validateEmail(value: unknown): value is string {
    if (typeof value !== 'string' || !value) return false;
    return EMAIL_REGEX.test(value);
  }

  /**
   * Validates a chilean RUT (format and check digit) outside a reactive form.
   *
   * `rutValidatorFn` covers form controls; this is for the Klap card forms, which collect the
   * cardholder RUT with plain template bindings.
   */
  isValidRut(rut: string): boolean {
    // Strip every separator, not just dots and dashes, so a RUT typed with spaces is accepted
    // here exactly as the API's `formatRUN` accepts it.
    const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (!/^\d+[0-9K]$/.test(cleanRut) || cleanRut.length < 2) return false;
    return cleanRut.slice(-1) === this.calculateDV(cleanRut.slice(0, -1));
  }

  /**
   * `accept` attribute for a file input restricted to `kinds`.
   *
   * The attribute only steers which files the picker highlights — a desktop picker still hands
   * back anything the user selects through "All files", so re-check the result with
   * `isAcceptedAttachment`.
   */
  attachmentAccept(kinds: AttachmentKind[]): string {
    return kinds
      .flatMap(kind => {
        const spec = ATTACHMENT_KIND_SPECS[kind];
        const mimeTypes = spec.mimePrefixes.map(prefix => prefix.endsWith('/') ? `${prefix}*` : prefix);
        return [...mimeTypes, ...spec.extensions];
      })
      .join(',');
  }

  /**
   * Whether `file` belongs to one of `kinds`, by MIME type or, failing that, by extension.
   *
   * This is a UX affordance: the server decides what it stores (guardrail 1).
   */
  isAcceptedAttachment(file: File, kinds: AttachmentKind[]): boolean {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();
    return kinds.some(kind => {
      const spec = ATTACHMENT_KIND_SPECS[kind];
      const mimeMatches = !!mimeType && spec.mimePrefixes.some(prefix => mimeType.startsWith(prefix));
      return mimeMatches || spec.extensions.some(extension => fileName.endsWith(extension));
    });
  }

  private calculateDV(rut: string): string {
    let sum = 0;
    let multiplier = 2;

    for (let i = rut.length - 1; i >= 0; i--) {
      sum += parseInt(rut.charAt(i), 10) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }

    const remainder = sum % 11;
    const dv = 11 - remainder;

    if (dv === 11) return '0';
    if (dv === 10) return 'K';
    return dv.toString();
  }
}
